import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Helmet } from "react-helmet";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import TextField from "../../components/Form/TextField";
import Button from "../../components/Buttons/Button";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../lib/i18next";
import {
  startRegisterAppSumoUser,
  startRequestAppSumoReactivationOtp,
  startReactivateAppSumoUser,
} from "../../actions/auth";
import RegisterVerificationStep from "../Register/components/RegisterVerificationStep";
import {
  APPSUMO_REGISTER_TITLE,
  APPSUMO_REGISTER_SUBTITLE,
  APPSUMO_REGISTER_BUTTON_SUBMIT,
  APPSUMO_REGISTER_SESSION_EXPIRED,
  APP_SUMO_REACTIVATION_NOT_ELIGIBLE,
  APPSUMO_REACTIVATION_OTP_TITLE,
  APPSUMO_REACTIVATION_OTP_EMAIL_SENT,
  APPSUMO_CALLBACK_BUTTON_RETURN_TO_APPSUMO,
  EMAIL_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  PASSWORD_CONFIRMATION_PLACEHOLDER,
  FIELD_REQUIRED,
  FIELD_EMAIL_VALID,
  FIELD_PASSWORD_INSECURE,
  FIELD_PASSWORDS_NO_MATCH,
  BUTTON_RESEND_CODE,
  BUTTON_VERIFY_CODE,
  ERROR_OCCURRED,
} from "../../i18n/keysTranslations";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../utils/regex";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";

const APPSUMO_LICENSE_KEY = "appSumoLicenseKey";
const APPSUMO_MARKETPLACE_URL = "https://appsumo.com/account/products";
const USER_REGISTERED_CODE = "userRegistered";

const REGISTER_STEPS = {
  FORM: "form",
  OTP: "otp",
};

const getMaskedEmail = ({ email = "" }) => {
  const emailNormalized = String(email).trim();
  const [localPart = "", domainPart = ""] = emailNormalized.split("@");
  if (!localPart || !domainPart) return "*****";

  const maskedLocalPart = `${localPart.charAt(0)}${"*".repeat(
    Math.max(localPart.length - 1, 5)
  )}`;
  return `${maskedLocalPart}@${domainPart}`;
};

const AppSumoRegister = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [licenseKey, setLicenseKey] = useState(null);
  const [currentStep, setCurrentStep] = useState(REGISTER_STEPS.FORM);
  const [countdown, setCountdown] = useState(30);
  const [isResendAllowed, setIsResendAllowed] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const { t, i18n } = useTranslationApp();

  const { control, handleSubmit, formState, watch, getValues } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      otpCode: "",
    },
    mode: "onSubmit",
  });
  const errors = formState.errors;
  useFocusErrorForm(formState);

  const showFormStep = currentStep === REGISTER_STEPS.FORM;
  const showOtpStep = currentStep === REGISTER_STEPS.OTP;
  const emailMasked = getMaskedEmail({ email: watch("email") });
  const language = i18n?.language?.startsWith("es") ? "es" : "en";

  useEffect(() => {
    const storedKey = sessionStorage.getItem(APPSUMO_LICENSE_KEY);
    if (!storedKey) {
      setError(t(APPSUMO_REGISTER_SESSION_EXPIRED));
      return;
    }
    setLicenseKey(storedKey);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!showOtpStep || isResendAllowed) return undefined;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsResendAllowed(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showOtpStep, isResendAllowed]);

  const onChangeShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const requestReactivationOtp = async ({ email }) => {
    const response = await dispatch(
      startRequestAppSumoReactivationOtp({
        email,
        lng: language,
      })
    );

    if (!response?.ok) {
      setError(response?.message || t(APP_SUMO_REACTIVATION_NOT_ELIGIBLE));
      return false;
    }

    setError(null);
    setCurrentStep(REGISTER_STEPS.OTP);
    setCountdown(30);
    setIsResendAllowed(false);
    return true;
  };

  const onRegister = async ({ email, password }) => {
    const response = await dispatch(
      startRegisterAppSumoUser({ email, password, licenseKey })
    );

    if (response?.ok) {
      sessionStorage.removeItem(APPSUMO_LICENSE_KEY);
      navigate("/settings", { replace: true });
      return;
    }

    if (response?.code === USER_REGISTERED_CODE) {
      await requestReactivationOtp({ email });
      return;
    }

    setError(response?.message || t(APPSUMO_REGISTER_SESSION_EXPIRED));
  };

  const onReactivate = async () => {
    const { email, password, otpCode } = getValues();
    const response = await dispatch(
      startReactivateAppSumoUser({
        email,
        password,
        licenseKey,
        otpCode,
      })
    );

    if (!response?.ok) {
      setError(response?.message || t(ERROR_OCCURRED));
      return;
    }

    sessionStorage.removeItem(APPSUMO_LICENSE_KEY);
    navigate("/settings", { replace: true });
  };

  const resendCode = async () => {
    if (!isResendAllowed) return;

    setIsResendingCode(true);
    const { email } = getValues();
    await requestReactivationOtp({ email });
    setIsResendingCode(false);
    setCountdown(30);
    setIsResendAllowed(false);
  };

  const onSubmit = async (formValues) => {
    if (!licenseKey) {
      setError(t(APPSUMO_REGISTER_SESSION_EXPIRED));
      return;
    }

    setError(null);
    setIsFetching(true);

    try {
      if (showOtpStep) {
        await onReactivate();
        return;
      }

      await onRegister(formValues);
    } finally {
      setIsFetching(false);
    }
  };

  const containerStyles = {
    width: "100%",
    maxWidth: "450px",
    py: 5,
    px: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "background.paper",
    border: `1px solid ${INPUT_COLOR_BORDER}`,
    borderRadius: 3,
    boxShadow: "0px 0px 50px 0px rgba(0, 0, 0, 0.1)",
  };

  const logoStyles = {
    width: "auto",
    height: "auto",
    maxHeight: "60px",
    ".MuiAvatar-img": {
      maxWidth: "160px",
      objectFit: "contain",
    },
    mb: 1,
  };

  const isSessionExpired = !licenseKey && error;

  return (
    <Container
      maxWidth="xs"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box sx={containerStyles}>
        <Helmet>
          <title>{`${theme.nameBusiness} - AppSumo`}</title>
        </Helmet>
        <Avatar
          src={theme.logoLogin}
          alt="Logo"
          variant="square"
          sx={logoStyles}
        />

        {isSessionExpired ? (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={3}
              mt={1}
              textAlign="center"
            >
              {t(APPSUMO_REGISTER_SESSION_EXPIRED)}
            </Typography>
            <Box
              component="a"
              href={APPSUMO_MARKETPLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none", width: "100%" }}
            >
              <Button variant="contained" color="primary" fullWidth>
                {t(APPSUMO_CALLBACK_BUTTON_RETURN_TO_APPSUMO)}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {showFormStep && (
              <>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  mt={1}
                  mb={0.5}
                  textAlign="center"
                >
                  {t(APPSUMO_REGISTER_TITLE)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={2}
                  textAlign="center"
                >
                  {t(APPSUMO_REGISTER_SUBTITLE)}
                </Typography>
              </>
            )}

            {showOtpStep && (
              <>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  mt={1}
                  mb={0.5}
                  textAlign="center"
                >
                  {t(APPSUMO_REACTIVATION_OTP_TITLE)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={2}
                  textAlign="center"
                >
                  {t(APPSUMO_REACTIVATION_OTP_EMAIL_SENT, { emailMasked })}
                </Typography>
              </>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 1.5,
              }}
            >
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 1,
                    backgroundColor: "fields.background",
                    color: "background.paperContrastText",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    borderRadius: BORDER_RADIUS,
                    boxShadow: BOX_SHADOW_FIELDS,
                  }}
                >
                  {error}
                </Alert>
              )}

              {showFormStep && (
                <>
                  <TextField
                    fullWidth
                    id="email"
                    label={t(EMAIL_PLACEHOLDER)}
                    name="email"
                    variant="filled"
                    color="primary"
                    InputLabelProps={{ shrink: true }}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      validate: {
                        emailValid: (value) => {
                          if (!REGEX_EMAIL.test(value)) {
                            return t(FIELD_EMAIL_VALID);
                          }
                        },
                      },
                    }}
                    errors={errors}
                  />

                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label={t(PASSWORD_PLACEHOLDER)}
                    type={showPassword ? "text" : "password"}
                    variant="filled"
                    color="primary"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={onChangeShowPassword}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      validate: (value) => {
                        if (!REGEX_PASSWORD.test(value)) {
                          return t(FIELD_PASSWORD_INSECURE);
                        }
                      },
                    }}
                    errors={errors}
                  />

                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    label={t(PASSWORD_CONFIRMATION_PLACEHOLDER)}
                    type={showPassword ? "text" : "password"}
                    variant="filled"
                    color="primary"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={onChangeShowPassword}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      validate: (value) => {
                        const password = watch("password");
                        if (value !== password) {
                          return t(FIELD_PASSWORDS_NO_MATCH);
                        }
                      },
                    }}
                    errors={errors}
                  />

                  <Button
                    loading={isFetching}
                    type="submit"
                    color="primary"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                  >
                    {t(APPSUMO_REGISTER_BUTTON_SUBMIT)}
                  </Button>
                </>
              )}

              {showOtpStep && (
                <>
                  <RegisterVerificationStep
                    control={control}
                    errors={errors}
                    emailMasked={emailMasked}
                    hideHeader
                  />

                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button
                      loading={isResendingCode}
                      onClick={resendCode}
                      disabled={!isResendAllowed || isFetching}
                      color="secondary"
                      fullWidth
                      variant="contained"
                    >
                      {`${t(BUTTON_RESEND_CODE)}${
                        isResendAllowed ? "" : ` (${countdown})`
                      }`}
                    </Button>
                    <Button
                      loading={isFetching}
                      type="submit"
                      color="primary"
                      fullWidth
                      variant="contained"
                    >
                      {t(BUTTON_VERIFY_CODE)}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default AppSumoRegister;
